import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertCircle, Check, ChevronDown, ChevronRight, Copy, RefreshCw } from "lucide-react";
import { useState } from "react";

import { apiClient } from "@/lib/api";
import { describe } from "@/lib/api-error";
import { cn } from "@/lib/utils";
import type { ApiLogEntry, ApiLogParams } from "@tilt/admin-sdk";

/**
 * API Log viewer + pos.system.api_debug_log toggle.
 *
 * ApiLogToggle is used by ConfigurationSettingsTab (Settings tab).
 * ApiLogTab is the standalone log viewer page (System → API logs).
 * Super_admin only.
 */

const PROVIDERS = ["", "valor", "aeropay"];
const HTTP_METHODS = ["", "GET", "POST", "PUT", "PATCH", "DELETE"];
const OPERATIONS = [
  "",
  "initiate",
  "poll",
  "refund",
  "void",
  "iframe_token",
  "settlement_check",
  "webhook",
  "receipt",
];

const OPERATION_LABELS: Record<string, string> = {
  initiate: "Payment Initiation",
  poll: "Settlement Poll",
  refund: "Refund",
  void: "Void",
  iframe_token: "iFrame Token",
  settlement_check: "Settlement Check",
  webhook: "Webhook",
  receipt: "Receipt Fetch",
};

const OPERATION_COLORS: Record<string, string> = {
  initiate: "bg-blue-100 text-blue-800",
  poll: "bg-indigo-100 text-indigo-800",
  refund: "bg-orange-100 text-orange-800",
  void: "bg-red-100 text-red-800",
  iframe_token: "bg-slate-100 text-slate-700",
  settlement_check: "bg-purple-100 text-purple-800",
  webhook: "bg-teal-100 text-teal-800",
  receipt: "bg-green-100 text-green-800",
};

function nowMinus(ms: number): string {
  // datetime-local inputs expect YYYY-MM-DDTHH:mm in LOCAL time, not UTC.
  // Subtract timezone offset so the displayed value matches wall-clock time.
  const d = new Date(Date.now() - ms);
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}

function statusBadge(status: number | null): JSX.Element {
  if (status == null) return <span className="text-muted-foreground">-</span>;
  const cls =
    status >= 500
      ? "bg-destructive/10 text-destructive"
      : status >= 400
        ? "bg-amber-100 text-amber-800"
        : status >= 200
          ? "bg-green-100 text-green-800"
          : "bg-muted text-muted-foreground";
  return (
    <span className={cn("rounded px-1.5 py-0.5 font-mono text-[10px] font-medium", cls)}>
      {status}
    </span>
  );
}

function fmtMs(v: number | null): string {
  if (v == null) return "-";
  return v >= 1000 ? `${(v / 1000).toFixed(1)}s` : `${v}ms`;
}

function fmtDt(s: string): string {
  try {
    const d = new Date(s);
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch {
    return s;
  }
}

// ---------------------------------------------------------------- //
// Feature flag toggle - pos.system.api_debug_log
// ---------------------------------------------------------------- //

export function ApiLogToggle(): JSX.Element {
  const qc = useQueryClient();
  const featureQuery = useQuery({
    queryKey: ["feature", "pos.system.api_debug_log"],
    queryFn: () => apiClient.listFeatureCatalog(),
    select: (data) => data.features.find((f) => f.path === "pos.system.api_debug_log"),
    staleTime: 30_000,
  });

  const patch = useMutation({
    mutationFn: (policy: "broadcast" | "controlled") =>
      apiClient.patchFeatureRollout("pos.system.api_debug_log", { rollout_policy: policy }),
    onSuccess: () =>
      void qc.invalidateQueries({ queryKey: ["feature", "pos.system.api_debug_log"] }),
  });

  const feature = featureQuery.data;
  const isOn = feature?.rollout_policy === "broadcast";

  return (
    <div className="flex items-start justify-between gap-4 rounded-lg border bg-card p-4 shadow-sm">
      <div className="space-y-1">
        <p className="text-sm font-semibold">External API logging</p>
        <p className="text-xs text-muted-foreground">
          When <strong>on</strong> (broadcast), every outbound HTTP call from Valor, Aeropay, and
          other providers is written to{" "}
          <code className="font-mono text-[10px]">pos_api_log_tbl</code>
          and visible in the log viewer below. Turn off globally by switching to{" "}
          <strong>controlled</strong> - then only scopes with an explicit entitlement grant will
          log.
        </p>
        <p className="font-mono text-[10px] text-muted-foreground">
          feature: pos.system.api_debug_log · current policy:{" "}
          <span className={cn("font-semibold", isOn ? "text-green-700" : "text-amber-700")}>
            {feature?.rollout_policy ?? "-"}
          </span>
        </p>
      </div>
      <button
        type="button"
        disabled={patch.isPending || featureQuery.isLoading}
        onClick={() => patch.mutate(isOn ? "controlled" : "broadcast")}
        className={cn(
          "shrink-0 rounded-full px-4 py-1.5 text-xs font-semibold transition-colors",
          isOn
            ? "bg-green-600 text-white hover:bg-green-700"
            : "bg-muted text-foreground hover:bg-muted/80",
          "disabled:cursor-not-allowed disabled:opacity-50",
        )}
      >
        {patch.isPending ? "Saving…" : isOn ? "On (broadcast)" : "Off (controlled)"}
      </button>
    </div>
  );
}

function CopyButton({ text }: { readonly text: string }): JSX.Element {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={() => {
        void navigator.clipboard.writeText(text).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        });
      }}
      className="ml-1.5 inline-flex shrink-0 items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground hover:bg-muted"
      title="Copy to clipboard"
    >
      {copied ? <Check className="h-3 w-3 text-green-600" /> : <Copy className="h-3 w-3" />}
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

// ---------------------------------------------------------------- //
// Log row detail expander
// ---------------------------------------------------------------- //

function LogRow({ entry }: { readonly entry: ApiLogEntry }): JSX.Element {
  const [open, setOpen] = useState(false);

  return (
    <li className="divide-y">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-3 px-3 py-2.5 text-left hover:bg-muted/50"
      >
        <span className="shrink-0 text-muted-foreground">
          {open ? (
            <ChevronDown className="h-3.5 w-3.5" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5" />
          )}
        </span>
        {/* Leading identity: Operation label + provider */}
        <span
          className={cn(
            "shrink-0 rounded px-2 py-0.5 text-[10px] font-semibold",
            entry.operation
              ? (OPERATION_COLORS[entry.operation] ?? "bg-muted text-muted-foreground")
              : "bg-muted text-muted-foreground",
          )}
        >
          {entry.operation ? (OPERATION_LABELS[entry.operation] ?? entry.operation) : "Unknown"}
        </span>
        {entry.provider && (
          <span className="shrink-0 font-mono text-[10px] font-medium text-muted-foreground">
            {entry.provider}
          </span>
        )}
        {/* Transaction context - order ref or payment ref if present */}
        {(entry.order_ref ?? entry.payment_ref) && (
          <span className="shrink-0 rounded border px-1.5 py-0.5 font-mono text-[10px] text-foreground">
            {entry.order_ref ?? entry.payment_ref}
          </span>
        )}
        <span className="w-10 shrink-0 font-mono text-[10px] text-muted-foreground">
          {entry.http_method}
        </span>
        <span className="min-w-0 flex-1 truncate font-mono text-[10px] text-muted-foreground">
          {entry.url}
        </span>
        <span className="shrink-0">{statusBadge(entry.http_status)}</span>
        <span className="w-14 shrink-0 text-right font-mono text-[10px] text-muted-foreground">
          {fmtMs(entry.duration_ms)}
        </span>
        {entry.error_message && (
          <AlertCircle className="h-3.5 w-3.5 shrink-0 text-destructive" aria-label="Error" />
        )}
        <span className="w-36 shrink-0 text-right text-[10px] text-muted-foreground">
          {fmtDt(entry.created_at)}
        </span>
      </button>

      {open && (
        <div className="grid gap-3 bg-muted/30 px-4 py-3 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              Full URL
            </p>
            <div className="flex items-start gap-1 rounded border bg-background p-2">
              <span className="min-w-0 flex-1 break-all font-mono text-[10px]">{entry.url}</span>
              {entry.url && <CopyButton text={entry.url} />}
            </div>
          </div>
          <div>
            <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              Request body
            </p>
            <pre className="max-h-64 overflow-auto rounded border bg-background p-2 text-[10px]">
              {entry.request_body != null ? JSON.stringify(entry.request_body, null, 2) : "(empty)"}
            </pre>
          </div>
          <div>
            <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              Response body
            </p>
            <pre className="max-h-64 overflow-auto rounded border bg-background p-2 text-[10px]">
              {entry.response_body != null
                ? JSON.stringify(entry.response_body, null, 2)
                : "(empty)"}
            </pre>
          </div>
          {entry.error_message && (
            <div className="sm:col-span-2">
              <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-destructive">
                Error
              </p>
              <p className="font-mono text-xs text-destructive">{entry.error_message}</p>
            </div>
          )}
          <div className="space-y-1 sm:col-span-2">
            <p className="text-[10px] text-muted-foreground">
              <span className="font-medium">Provider:</span> {entry.provider}
              {entry.operation && (
                <>
                  {" "}
                  · <span className="font-medium">Operation:</span>{" "}
                  <span className="rounded bg-muted px-1 font-mono">{entry.operation}</span>
                </>
              )}{" "}
              · <span className="font-medium">Payment ID:</span> {entry.payment_id ?? "-"}
              {entry.order_id && (
                <>
                  {" "}
                  · <span className="font-medium">Order ID:</span>{" "}
                  <span className="font-mono">{entry.order_id}</span>
                </>
              )}
              {entry.order_ref && (
                <>
                  {" "}
                  · <span className="font-medium">Order Ref:</span> {entry.order_ref}
                </>
              )}
              {entry.payment_ref && (
                <>
                  {" "}
                  · <span className="font-medium">Payment Ref:</span>{" "}
                  <span className="font-mono">{entry.payment_ref}</span>
                </>
              )}
              {entry.location_id && (
                <>
                  {" "}
                  · <span className="font-medium">Location:</span>{" "}
                  <span className="font-mono">{entry.location_id}</span>
                </>
              )}{" "}
              · <span className="font-medium">Correlation:</span> {entry.correlation_id ?? "-"} ·{" "}
              <span className="font-medium">ID:</span> <span className="font-mono">{entry.id}</span>
            </p>
          </div>
        </div>
      )}
    </li>
  );
}

// ---------------------------------------------------------------- //
// Main tab component
// ---------------------------------------------------------------- //

function setOpt<K extends keyof ApiLogParams>(
  key: K,
  value: ApiLogParams[K] | undefined,
  prev: ApiLogParams,
): ApiLogParams {
  if (value === undefined) {
    const { [key]: _removed, ...rest } = prev;
    return rest as ApiLogParams;
  }
  return { ...prev, [key]: value } as ApiLogParams;
}

export function ApiLogTab(): JSX.Element {
  const [filters, setFilters] = useState<ApiLogParams>({ limit: 50 });
  const [draft, setDraft] = useState<ApiLogParams>({ limit: 50 });
  const [cursor, setCursor] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ["api-logs", filters, cursor],
    queryFn: () =>
      cursor !== null
        ? apiClient.listApiLogs({ ...filters, cursor } as ApiLogParams)
        : apiClient.listApiLogs(filters),
    staleTime: 10_000,
  });

  function applyFilters() {
    setCursor(null);
    setFilters({ ...draft });
  }

  const data = query.data;

  return (
    <div className="space-y-5">
      {/* Filter bar */}
      <div className="rounded-lg border bg-card p-3 shadow-sm">
        <p className="mb-3 text-xs font-semibold text-muted-foreground">Filters</p>
        {/* Row 1: provider + method + operation + error state */}
        <div className="flex flex-wrap gap-2">
          <select
            value={draft.provider ?? ""}
            onChange={(e) => setDraft((d) => setOpt("provider", e.target.value || undefined, d))}
            className="rounded-md border bg-background px-2 py-1 text-xs"
            aria-label="Provider"
          >
            {PROVIDERS.map((p) => (
              <option key={p} value={p}>
                {p || "All providers"}
              </option>
            ))}
          </select>

          <select
            value={draft.http_method ?? ""}
            onChange={(e) => setDraft((d) => setOpt("http_method", e.target.value || undefined, d))}
            className="rounded-md border bg-background px-2 py-1 text-xs"
            aria-label="HTTP method"
          >
            {HTTP_METHODS.map((m) => (
              <option key={m} value={m}>
                {m || "All methods"}
              </option>
            ))}
          </select>

          <select
            value={draft.operation ?? ""}
            onChange={(e) => setDraft((d) => setOpt("operation", e.target.value || undefined, d))}
            className="rounded-md border bg-background px-2 py-1 text-xs"
            aria-label="Operation"
          >
            {OPERATIONS.map((op) => (
              <option key={op} value={op}>
                {op ? (OPERATION_LABELS[op] ?? op) : "All operations"}
              </option>
            ))}
          </select>

          <input
            type="number"
            placeholder="Status ≥"
            value={draft.status_gte ?? ""}
            onChange={(e) =>
              setDraft((d) =>
                setOpt("status_gte", e.target.value ? Number(e.target.value) : undefined, d),
              )
            }
            className="w-24 rounded-md border bg-background px-2 py-1 text-xs"
            min={100}
            max={599}
          />

          <select
            value={draft.has_error === undefined ? "" : draft.has_error ? "true" : "false"}
            onChange={(e) =>
              setDraft((d) =>
                setOpt(
                  "has_error",
                  e.target.value === "" ? undefined : e.target.value === "true",
                  d,
                ),
              )
            }
            className="rounded-md border bg-background px-2 py-1 text-xs"
            aria-label="Has error"
          >
            <option value="">Any error state</option>
            <option value="true">Errors only</option>
            <option value="false">No errors</option>
          </select>
        </div>

        {/* Row 2: order + payment identifiers */}
        <div className="mt-2 flex flex-wrap gap-2">
          <input
            type="text"
            placeholder="Order ref (RO#, stock#…)"
            value={draft.order_ref ?? ""}
            onChange={(e) => setDraft((d) => setOpt("order_ref", e.target.value || undefined, d))}
            className="w-48 rounded-md border bg-background px-2 py-1 text-xs"
          />

          <input
            type="text"
            placeholder="Payment ref"
            value={draft.payment_ref ?? ""}
            onChange={(e) => setDraft((d) => setOpt("payment_ref", e.target.value || undefined, d))}
            className="w-44 rounded-md border bg-background px-2 py-1 text-xs"
          />

          <input
            type="text"
            placeholder="Payment ID (UUID)"
            value={draft.payment_id ?? ""}
            onChange={(e) => setDraft((d) => setOpt("payment_id", e.target.value || undefined, d))}
            className="w-64 rounded-md border bg-background px-2 py-1 font-mono text-xs"
          />

          <input
            type="text"
            placeholder="Order ID (UUID)"
            value={draft.order_id ?? ""}
            onChange={(e) => setDraft((d) => setOpt("order_id", e.target.value || undefined, d))}
            className="w-64 rounded-md border bg-background px-2 py-1 font-mono text-xs"
          />

          <input
            type="text"
            placeholder="Location ID (UUID)"
            value={draft.location_id ?? ""}
            onChange={(e) => setDraft((d) => setOpt("location_id", e.target.value || undefined, d))}
            className="w-64 rounded-md border bg-background px-2 py-1 font-mono text-xs"
          />
        </div>

        {/* Row 3: time range + quick presets + actions */}
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {/* Quick time presets */}
          <div className="flex gap-1">
            {(
              [
                ["1m", 60_000],
                ["5m", 5 * 60_000],
                ["1h", 60 * 60_000],
              ] as const
            ).map(([label, ms]) => (
              <button
                key={label}
                type="button"
                onClick={() => {
                  const newSince = nowMinus(ms);
                  const applyPreset = (d: ApiLogParams): ApiLogParams => {
                    const { until: _removed, ...rest } = d;
                    return { ...rest, since: newSince };
                  };
                  setDraft(applyPreset);
                  setFilters(applyPreset);
                  setCursor(null);
                }}
                className="rounded border bg-muted px-2 py-0.5 text-[10px] font-medium hover:bg-muted/80"
              >
                Last {label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-1">
            <label htmlFor="filter-since" className="text-[10px] text-muted-foreground">
              Since
            </label>
            <input
              id="filter-since"
              type="text"
              placeholder="2026-05-11T14:30"
              value={draft.since ?? ""}
              onChange={(e) => setDraft((d) => setOpt("since", e.target.value || undefined, d))}
              className="rounded-md border bg-background px-2 py-1 font-mono text-[10px]"
            />
          </div>
          <div className="flex items-center gap-1">
            <label htmlFor="filter-until" className="text-[10px] text-muted-foreground">
              Until
            </label>
            <input
              id="filter-until"
              type="text"
              placeholder="2026-05-11T15:00"
              value={draft.until ?? ""}
              onChange={(e) => setDraft((d) => setOpt("until", e.target.value || undefined, d))}
              className="rounded-md border bg-background px-2 py-1 font-mono text-[10px]"
            />
          </div>
          <button
            type="button"
            onClick={applyFilters}
            className="rounded-md bg-primary px-3 py-1 text-xs font-medium text-primary-foreground hover:opacity-90"
          >
            Apply
          </button>
          <button
            type="button"
            onClick={() => {
              const reset: ApiLogParams = { limit: 50 };
              setDraft(reset);
              setFilters(reset);
              setCursor(null);
            }}
            className="rounded-md border bg-background px-3 py-1 text-xs hover:bg-muted"
          >
            Reset
          </button>
          <button
            type="button"
            onClick={() => void query.refetch()}
            disabled={query.isFetching}
            className="ml-auto inline-flex items-center gap-1 rounded-md border bg-background px-2 py-1 text-xs hover:bg-muted disabled:opacity-50"
            aria-label="Refresh"
          >
            <RefreshCw className={cn("h-3 w-3", query.isFetching && "animate-spin")} />
            Refresh
          </button>
        </div>
      </div>

      {/* Results */}
      {query.isError && (
        <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
          {describe(query.error)}
        </div>
      )}

      {query.isLoading ? (
        <p className="py-8 text-center text-sm text-muted-foreground">Loading…</p>
      ) : !data || data.items.length === 0 ? (
        <p className="py-8 text-center text-sm text-muted-foreground">
          No log entries match the current filters.
        </p>
      ) : (
        <div className="overflow-hidden rounded-lg border shadow-sm">
          <div className="flex items-center justify-between border-b bg-muted/30 px-3 py-2">
            <p className="text-xs text-muted-foreground">
              {data.count} {data.count === 1 ? "entry" : "entries"}
              {data.has_more && " (more available - load next page)"}
            </p>
          </div>
          <ul className="divide-y">
            {data.items.map((entry) => (
              <LogRow key={entry.id} entry={entry} />
            ))}
          </ul>
          {data.has_more && data.next_cursor && (
            <div className="border-t bg-muted/10 px-3 py-2">
              <button
                type="button"
                onClick={() => setCursor(data.next_cursor ?? null)}
                className="text-xs text-primary hover:underline"
              >
                Load next page →
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
